import * as React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"

import { AppStore } from "../../store"
import OldUIContext from "../../olduicontext"

import * as actions from "./actions"

import {
    initializeBookmarks,
    initializeBookmarkButton,
} from "../bookmarks/actions"
import * as notifications from "../notifications/actions"

import Header from "./components/Header"
import { IButton, ICallButton } from "../common/contextmenu"
import { IBreadcrumb, IFieldValue, IHeader } from "./index"
import { IOldUIContext, IOldUIIPEField, ITenForceWindow } from "../../global"

declare let window: ITenForceWindow

interface OldUIButtonInit {
    buttons: Omit<IButton, "isLoading" | "uuid">[]
}

interface OldUIBreadcrumbInit {
    breadcrumbs: IBreadcrumb[]
}

type OldUIProps = Partial<IHeader> & OldUIButtonInit & OldUIBreadcrumbInit

interface OldUIInit {
    start: (element: HTMLElement | undefined | null, props: OldUIProps) => void
    updateButtonScripts: (
        scriptIds?: number[],
    ) => actions.ToggleButtonScriptsVisibility
    updateElementsState: (values: IOldUIIPEField[]) => void
    updateElements: (values: IOldUIIPEField[]) => void
    increaseAttachmentsCount: (count: number) => actions.IncreaseCount
    decreaseAttachmentsCount: () => actions.IncreaseCount
    increaseCommentsCount: () => actions.IncreaseCount
    decreaseCommentsCount: () => actions.IncreaseCount
    updateButtonVisibility: (
        id: string,
        visibility: boolean,
    ) => actions.UpdateButtonVisibility
    executeCallButton: (buttonId: string) => void
}

const isCurrentResourceTitleValue = (
    titleField: IFieldValue,
    value: IOldUIIPEField,
): boolean => {
    if (titleField && titleField.ipe) {
        const titleFieldId = `${titleField.ipe.object.toLowerCase()}.${
            titleField.ipe.objectId
        }.${titleField.ipe.propertyId}`
        return value.ID === titleFieldId || value.FieldId === titleFieldId
    }
    return false
}

const getStatusRegex = (id: number): RegExp =>
    new RegExp("^(item|meeting)." + id.toString() + ".7$")

export const isCurrentItemStatusValue = (
    itemId: number | undefined,
    value: IOldUIIPEField,
): boolean => {
    return itemId !== undefined && !!value.ID
        ? getStatusRegex(itemId).test(value.ID)
        : false
}

const getConceptRegex = (id: number): RegExp =>
    new RegExp("^(item|meeting)." + id.toString() + ".19$")

export const isCurrentItemConceptValue = (
    itemId: number | undefined,
    value: IOldUIIPEField,
): boolean => {
    return itemId !== undefined && !!value.ID
        ? getConceptRegex(itemId).test(value.ID)
        : false
}

const unescapeUpdateElementsValues = (s: string): string =>
    s
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")

const init = (store: AppStore, oldUIContext: IOldUIContext): OldUIInit => {
    const start = (
        element: HTMLElement | undefined | null,
        props: OldUIProps,
    ): void => {
        if (!element) {
            throw new Error("Element should have value")
        }

        let activePanel = window.location.hash.substr(1)
        if (activePanel.length === 0) {
            activePanel = "overview"
        }

        const { buttons, breadcrumbs } = props
        store.dispatch(
            actions.initialize(props, activePanel, buttons, breadcrumbs),
        )

        const { selectionFilterId, selectionViewType } = window.env
        if (!!selectionFilterId) {
            store.dispatch(
                actions.requestListViews(selectionFilterId, selectionViewType),
            )
        }

        if (!!props.resource && !!props.headerTypes) {
            const {
                resource: { id, bookmarkId, resourceType, workspaceId },
                showBookmarkButton,
            } = props
            const bookmarkUrl = window.location.pathname

            if (
                showBookmarkButton &&
                id !== undefined &&
                !!resourceType &&
                bookmarkId !== undefined
            ) {
                const bookmarkButtonId = "HEADER_BOOKMARK_BUTTON"
                store.dispatch(initializeBookmarks())
                store.dispatch(
                    initializeBookmarkButton(
                        bookmarkButtonId,
                        bookmarkId,
                        resourceType,
                        id,
                        workspaceId,
                        null,
                        bookmarkUrl,
                    ),
                )
                store.dispatch(
                    actions.initializeBookmarkButtonId(bookmarkButtonId),
                )
            }
        }

        const root = createRoot(element)
        root.render(
            <Provider store={store}>
                <OldUIContext.Provider value={oldUIContext}>
                    <Header />
                </OldUIContext.Provider>
            </Provider>,
        )
    }

    // TODO Move it to `resourceHeader` "namespace" like action registration?
    const updateButtonScripts = (
        scriptIds?: number[],
    ): actions.ToggleButtonScriptsVisibility =>
        store.dispatch(actions.toggleButtonScriptsVisibility(scriptIds || []))

    const updateElementsState = (values: IOldUIIPEField[]): void => {
        const {
            resourceHeader: { resource },
        } = store.getState()

        const { title } = resource
        values.forEach((value) => {
            if (isCurrentResourceTitleValue(title, value)) {
                store.dispatch(
                    actions.refreshTitle({
                        readOnly: value.IsReadOnly,
                        hidden: value.IsHidden,
                    }),
                )
            }
        })
    }

    const updateElements = (values: IOldUIIPEField[]): void => {
        const {
            resourceHeader: {
                resource: { title, id, resourceType },
            },
        } = store.getState()

        values.forEach((value) => {
            if (isCurrentResourceTitleValue(title, value)) {
                store.dispatch(
                    actions.refreshTitle({
                        value: unescapeUpdateElementsValues(value.Value),
                    }),
                )
            }

            if (resourceType == "Item") {
                if (isCurrentItemStatusValue(id, value)) {
                    store.dispatch(
                        actions.refreshStatus(
                            unescapeUpdateElementsValues(value.Value),
                        ),
                    )
                }

                if (isCurrentItemConceptValue(id, value)) {
                    // Example value:
                    //"W 637880332287998707 : <a href=\"/workspace/2282/2357l\">L637880332394l 889435</a>"

                    // Find the concept ID in the href of the anchor
                    const conceptIdRegex = new RegExp(/(?!\/)(\d)+(?=(l|f)("))/)
                    const conceptId = value.Value.match(conceptIdRegex)

                    // Find the workspace ID in the href of the anchor
                    const workspaceIdRegex = new RegExp(/(?!\/)(\d)+(?=(\/))/)
                    const workspaceId = value.Value.match(workspaceIdRegex)

                    // Find the name of the concept inside the anchor
                    const conceptLabelRegex = new RegExp(
                        /(?!>)(\w|\d|\s)+(?=<\/a>)/,
                    )
                    const conceptLabel = value.Value.match(conceptLabelRegex)

                    // the " : " is always present between the workspace and the anchor
                    const workspaceLabel = value.Value.split(" : <a")[0]

                    if (
                        !!workspaceId &&
                        workspaceId.length > 0 &&
                        !!workspaceLabel &&
                        workspaceLabel.length > 0 &&
                        !!conceptId &&
                        conceptId.length > 0 &&
                        !!conceptLabel &&
                        conceptLabel.length > 0
                    ) {
                        store.dispatch(
                            actions.refreshConceptBreadcrumb(
                                parseInt(workspaceId[0]),
                                workspaceLabel,
                                parseInt(conceptId[0]),
                                conceptLabel[0],
                            ),
                        )
                    }
                }
            }
        })
    }

    const increaseAttachmentsCount = (count = 1): actions.IncreaseCount =>
        store.dispatch(actions.increaseAttachmentsCount(count))

    const decreaseAttachmentsCount = (): actions.IncreaseCount =>
        store.dispatch(actions.increaseAttachmentsCount(-1))

    const increaseCommentsCount = (): actions.IncreaseCount =>
        store.dispatch(actions.increaseCommentsCount(1))

    const decreaseCommentsCount = (): actions.IncreaseCount =>
        store.dispatch(actions.increaseCommentsCount(-1))

    const updateButtonVisibility = (
        id: string,
        visibility: boolean,
    ): actions.UpdateButtonVisibility =>
        store.dispatch(actions.updateButtonVisibility(id, visibility))

    const executeCallButton = (buttonId: string): void => {
        const {
            resourceHeader: { buttons },
        } = store.getState()
        const buttonsArray = Object.values(buttons)
        const callButtons = buttonsArray.filter(
            (b) => b.buttonData.type === "Call",
        ) as ICallButton[]
        const button = callButtons.find((b) => b.id === buttonId)
        if (button) {
            store.dispatch(actions.executeCallButton(button))
        } else {
            const errorMessage =
                "Something went wrong while trying to execute a button."
            store.dispatch(notifications.addErrorNotification(errorMessage))
            console.error(`${errorMessage} There was no corresponding button found in the state.
                Provided buttonId: ${buttonId}, current buttons: ${JSON.stringify(
                buttons,
            )}.`)
        }
    }

    return {
        start,
        updateElementsState,
        updateButtonScripts,
        updateElements,
        increaseAttachmentsCount,
        decreaseAttachmentsCount,
        increaseCommentsCount,
        decreaseCommentsCount,
        updateButtonVisibility,
        executeCallButton,
    }
}

export default init
